import {
  getFilenameFromUrl,
  imageURLsFromMarkup,
  spacesToSnakeCase,
  videoURLsFromMarkup,
} from "helpers";
import {
  ICreativeNative,
  ICreativeRichMedia,
  ICreativeVideoVast,
  TCreative,
} from "./creative";
import { CREATIVE_TYPES } from "./creativeTypes";
import { getVastConfigFromTag } from "./helpers/vast";

//queryparam that makes Cloudfront return file cached under this url.
//So that update param to avoid getting previously cached one after overwrite
export const CREATIVE_ASSET_CACHE_QUERY_PARAM = "cacheV";

export const constructCacheSrcByUrl = (src, cacheVersions = {}) =>
  src &&
  `${src}?${CREATIVE_ASSET_CACHE_QUERY_PARAM}=${
    cacheVersions[getFilenameFromUrl(src)] || "0"
  }`;

export type IAssetsMap = { [assetName: string]: TCreative[] };
//Potentially long operation, so it is made async and non-blocking
//Takes ~7 seconds on 50 creatives
export const getAssetsToCreativesMapAsync = (creatives: TCreative[]) => {
  return new Promise<IAssetsMap>((resolve) => {
    if (!creatives?.length) {
      resolve({});
    }

    const searchLimit = creatives.length - 2;
    const map = {};
    const doNextCalc = (i) =>
      setTimeout(() => {
        accumulateAssetsFromCreative(map, creatives[i]);
        if (i > searchLimit) {
          resolve(map);
        } else {
          doNextCalc(i + 1);
        }
      }, 0);

    doNextCalc(0);
  });
};
const accumulateAssetsFromCreative = (acc: IAssetsMap, c: TCreative) => {
  if (!c) return;

  let assets;

  if (c.creativeType === CREATIVE_TYPES.RICH_MEDIA) {
    const markup = (c as ICreativeRichMedia).markup;
    if (markup) {
      assets = [...imageURLsFromMarkup(markup), ...videoURLsFromMarkup(markup)];
    }
  }

  if (c.creativeType === CREATIVE_TYPES.NATIVE) {
    if ((c as ICreativeNative).assets?.length) {
      assets = (c as ICreativeNative).assets
        .map((a) => (a as any)?.image?.url)
        .filter(Boolean);
    }
  }

  if (
    [
      CREATIVE_TYPES.VIDEO_VAST,
      CREATIVE_TYPES.VIDEO_VAST_HTML_EC,
    ].includes(c.creativeType)
  ) {
    const vastConfig = getVastConfigFromTag((c as ICreativeVideoVast).vast);
    if (vastConfig) {
      assets = [vastConfig.videoUrl, vastConfig?.companion?.src].filter(
        Boolean
      );
    }
  }

  assets?.forEach((a) => {
    const name = getFilenameFromUrl(a);
    acc[name] = acc[name] || [];
    acc[name].push(c);
  });

  return acc;
};

export const serverSideFormattedFilename = (filename: string) => {
  if (!filename) return filename;

  const dot = filename.lastIndexOf(".");
  const name = filename.substr(0, dot);
  const extension = filename.substr(dot);

  return spacesToSnakeCase(name)
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .concat(extension);
};

export type IPlayablesMap = { [playableId: string]: TCreative[] };
export function getPlayablesToCreativesMap(
  creatives: TCreative[]
): IPlayablesMap {
  const map = {};
  creatives.forEach((c) => {
    const playableId = (c as ICreativeVideoVast).playableId;
    if (
      [
        CREATIVE_TYPES.VIDEO_VAST,
        CREATIVE_TYPES.VIDEO_VAST_HTML_EC,
      ].includes(c.creativeType) &&
      playableId
    ) {
      map[playableId] = map[playableId] || [];
      map[playableId].push(c);
    }
  });

  return map;
}

export type Filename = string; //example, Card1.png
export type FormattedFilename = string; //uniquified, for example, Card1___9i1qwp.png
